// Chefi Super: perks, the plan (Super for 2 people, Family for 10), its members and invites, buying,
// gifts and redeeming codes. iOS/Android sell Super through the stores (no account there yet); the web
// and Telegram sell plans and gifts for the signed-in account (see platform/iap).
import React, { useEffect, useState } from 'react';
import { Text, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { useSettings } from '../store/settings';
import { useAccount } from '../store/account';
import { usePlan } from '../store/plan';
import { canManageSubscription, supportsPlanChanges, buySuper, loadOffers, manageInApp, manageSubscription, restoreSuper, storeAvailable, type SuperOffer } from '../platform/iap';
import { authAvailable } from '../platform/auth';
import { checkoutAvailable } from '../platform/checkout';
import { copyText } from '../platform/clipboard';
import { useNav, type AllParams } from '../navigation';
import { useRoute, type RouteProp } from '@react-navigation/native';
import Icon, { type IconName } from '../components/Icon';
import { confirm, notify } from '../components/Dialogs';
import LegalLinks from '../components/LegalLinks';
import { Body, Button, Caption, Field, ListItem, Screen, Section } from '../components/ui';
import { font, spacing, usePalette } from '../theme';
import type { PlanProduct } from '../types';

const PERKS: {icon:IconName;title:'super.perkCookbooks'|'super.perkFreeze'|'super.perkRepair'|'super.perkMediaArchive'|'super.perkFriendRepair'}[] = [
  {icon:'country',title:'super.perkCookbooks'},
  {icon:'freeze',title:'super.perkFreeze'},
  {icon:'reset',title:'super.perkRepair'},
  {icon:'image',title:'super.perkMediaArchive'},
  {icon:'buddies',title:'super.perkFriendRepair'},
];

export default function SuperScreen() {
  const { t } = useTranslation();
  const p = usePalette();
  const signedIn = useAccount(s => !!s.user);
  const { info, load, waitForPayment, confirming } = usePlan();
  const isSuper = useSettings(s => s.isSuper);
  const paid = useRoute<RouteProp<AllParams, 'Super'>>().params?.paid;
  const nav = useNav();
  useEffect(() => {
    if (signedIn) load();
  }, [signedIn, load]);
  // Back from the web checkout: the payment reaches the server by webhook, usually within seconds.
  useEffect(() => {
    if (!paid || !signedIn) return;
    nav.setParams({ paid: undefined });
    waitForPayment().then(ok => notify(t(ok ? 'plan.paymentDone' : 'plan.paymentSlow')));
  }, [paid, signedIn, nav, waitForPayment, t]);
  const plan = info?.plan ?? null;

  return (
    <Screen>
      <View style={{ alignItems: 'center', gap: spacing.s }}>
        <Icon name="super" size={56} color={p.super} />
        <Text style={{ fontSize: font.title, fontWeight: '800', color: p.text }}>{t('super.title')}</Text>
      </View>
      <Section>{PERKS.map(perk => <ListItem key={perk.title} title={t(perk.title)} icon={perk.icon} iconColor={p.super} />)}</Section>
      {confirming ? <Caption style={{ textAlign: 'center' }}>{t('plan.confirming')}</Caption> : null}
      {plan ? <PlanStatus /> : isSuper ? (
        <>
          <Body style={{ textAlign: 'center' }}>{t('super.active')}</Body>
          {!authAvailable && manageInApp ? <Button label={t('super.manage')} variant="secondary" onPress={manageSubscription} /> : null}
        </>
      ) : null}
      {plan?.role === 'owner' ? <Members /> : null}
      <Buy />
      {authAvailable ? (signedIn ? <><Gifts /><Redeem /></> : <SignInFirst />) : null}
      <Section><LegalLinks pages={['terms', 'refunds']} /></Section>
    </Screen>
  );
}

const planName = (t: (k: 'plan.super' | 'plan.family') => string, product: PlanProduct) => t(product === 'family' ? 'plan.family' : 'plan.super');

function PlanStatus() {
  const { t } = useTranslation();
  const nav = useNav();
  const { info, leave, busy } = usePlan();
  const plan = info!.plan!;
  const until = plan.until ? new Date(plan.until).toLocaleDateString() : null;
  const name = planName(t, plan.plan);
  return (
    <Section title={t('plan.yours')} icon="super">
      <Body>{plan.role === 'member' ? t('plan.memberOf', { owner: info!.owner?.displayName || t('plan.someone'), plan: name }) : t('plan.ownerOf', { plan: name, seats: plan.seats })}</Body>
      {until ? <Caption>{plan.source === 'gift' ? t('plan.giftUntil', { date: until }) : t('plan.until', { date: until })}</Caption> : null}
      {plan.role === 'member' ? (
        <Button label={t('plan.leave')} variant="secondary" disabled={busy} onPress={async () => {
          if (await confirm(t('plan.leaveTitle'), t('plan.leaveBody'), t('plan.leave'))) await leave();
        }} />
      ) : plan.source !== 'gift' ? (
        manageInApp && canManageSubscription(plan.source) ? (
          <Button label={t('super.manage')} variant="secondary" onPress={async () => {
            if (!(await manageSubscription())) notify(t('super.manageTelegram'));
          }} />
        ) : <Caption>{t('super.manageOriginal')}</Caption>
      ) : null}
      {plan.role === 'owner' && plan.source === 'stripe' && checkoutAvailable ? <Button label={t('checkout.chooseBilling')} variant="secondary" onPress={() => nav.navigate('Checkout', { offer: plan.plan })} /> : null}
    </Section>
  );
}

function Members() {
  const { t } = useTranslation();
  const p = usePalette();
  const { info, invite, remove, busy, error } = usePlan();
  const [link, setLink] = useState<string | null>(null);
  const plan = info!.plan!;
  const used = info!.members.length + 1;
  return (
    <Section title={t('plan.people')} icon="servings">
      <Caption>{t('plan.seatsUsed', { used, seats: plan.seats })}</Caption>
      <ListItem title={t('plan.you')} icon="user" />
      {info!.members.map(m => (
        <ListItem
          key={m.id} title={m.displayName || t('plan.someone')} subtitle={t('plan.joined', { date: new Date(m.joinedAt).toLocaleDateString() })} icon="user"
          right={<Icon name="trash" color={p.danger} />}
          onPress={async () => {
            if (await confirm(t('plan.removeTitle', { name: m.displayName || t('plan.someone') }), t('plan.removeBody'), t('plan.remove'))) await remove(m.id);
          }}
        />
      ))}
      {used < plan.seats ? (
        <Button label={t('plan.invite')} icon="link" variant="secondary" disabled={busy} onPress={async () => {
          const r = await invite();
          if (r) setLink(r.url);
        }} />
      ) : <Caption>{plan.plan === 'super' ? t('plan.fullSuper') : t('plan.fullFamily')}</Caption>}
      {link ? (
        <>
          <Body>{link}</Body>
          <Button label={t('plan.copyLink')} icon="copy" variant="plain" onPress={() => {
            copyText(link);
            notify(t('plan.copied'));
          }} />
          <Caption>{t('plan.inviteNote')}</Caption>
        </>
      ) : null}
      {error ? <Caption color={p.danger}>{t(`plan.errors.${error}` as 'plan.errors.generic')}</Caption> : null}
    </Section>
  );
}

function Buy() {
  const { t } = useTranslation();
  const signedIn = useAccount(s => !!s.user);
  const plan = usePlan(s => s.info?.plan ?? null);
  const refresh = usePlan(s => s.refresh);
  const { isSuper, set } = useSettings();
  const [offers, setOffers] = useState<SuperOffer[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const needsAccount = authAvailable && !signedIn;
  useEffect(() => {
    if (storeAvailable && !needsAccount) loadOffers().then(setOffers).catch(() => setOffers([]));
  }, [needsAccount]);
  if (!storeAvailable || needsAccount) return null;
  // Owners see only what is above their plan; without accounts (native) the store's own state decides.
  const shown = (offers ?? []).filter(o => !supportsPlanChanges && (plan || isSuper) ? false : plan?.role === 'owner' ? plan.plan === 'super' && o.product === 'family' : plan?.role !== 'member');
  if (offers && !offers.length) return <Body secondary style={{ textAlign: 'center' }}>{t('super.unavailable')}</Body>;
  return (
    <Section title={plan?.role === 'owner' ? t('plan.upgrade') : t('plan.choose')} icon="super">
      {offers === null ? <Caption>{t('super.loading')}</Caption> : null}
      {shown.map(o => (
        <View key={o.sku ?? o.product} style={{ gap: 4 }}>
          <Button
            label={o.price ? t('plan.buy', { plan: planName(t, o.product), price: o.price }) : t('plan.buyNoPrice', { plan: planName(t, o.product) })}
            icon="super" variant={o.product === 'super' ? 'super' : 'secondary'} big={o.product === 'super'} loading={busy === (o.sku ?? o.product)} disabled={!!busy}
            onPress={async () => {
              setBusy(o.sku ?? o.product);
              let ok = false;
              try { ok = await buySuper(o); }
              catch (e) {
                const code = (e as { code?: string })?.code;
                if (code === 'purchase_cancelled') return;
                notify(t(code === 'purchase_pending' || code === 'purchase_verifying' ? 'super.purchasePending' : 'super.purchaseFailed'));
                return;
              } finally { setBusy(null); }
              if (!ok) return notify(t('super.purchaseFailed'));
              if (!authAvailable) set({ isSuper: true });
              await refresh();
            }}
          />
          {o.interval ? <Caption style={{ textAlign: 'center' }}>{t(o.interval === 'year' ? 'checkout.billedAnnual' : 'checkout.billedMonthly')}</Caption> : null}
          <Caption style={{ textAlign: 'center' }}>{o.product === 'family' ? t('plan.familyNote') : t('plan.superNote')}</Caption>
        </View>
      ))}
      <Caption style={{ textAlign: 'center' }}>{offers?.some(o => !o.price) ? `${t('plan.priceAtCheckout')} ` : ''}{t('super.renewsPeriod')}</Caption>
      <Button label={t('super.restore')} variant="plain" loading={busy === 'restore'} disabled={!!busy} onPress={async () => {
        setBusy('restore');
        const ok = await restoreSuper().catch(() => false);
        setBusy(null);
        set({ isSuper: ok });
        if (authAvailable) await refresh();
      }} />
    </Section>
  );
}


function Gifts() {
  const { t } = useTranslation();
  const { info } = usePlan();
  const gifts = info?.gifts ?? [];
  if (!gifts.length) return null;
  return (
    <Section title={t('plan.gifts')} icon="gift">
      {gifts.map(g => (
        <ListItem
          key={g.id} icon="gift"
          title={`${planName(t, g.product)} · ${t('plan.months', { count: g.months })}`}
          subtitle={g.redeemed ? t('plan.giftRedeemed') : g.code ?? ''}
          right={g.url ? <Icon name="copy" /> : undefined}
          onPress={g.url ? () => {
            copyText(g.url!);
            notify(t('plan.copied'));
          } : undefined}
        />
      ))}
    </Section>
  );
}

/** Invite and gift codes, typed in (inside Telegram a link opens the browser, so typing is the easy way). */
export function Redeem({ initialCode = '', onDone }: { initialCode?: string; onDone?: () => void }) {
  const { t } = useTranslation();
  const p = usePalette();
  const { redeem, busy, error } = usePlan();
  const [code, setCode] = useState(initialCode);
  return (
    <Section title={t('plan.redeem')} icon="link">
      <Field label={t('plan.codeLabel')} value={code} onChangeText={setCode} placeholder="XXXX-XXXX" autoCapitalize="characters" autoCorrect={false} maxLength={16} />
      <Button label={t('plan.redeemButton')} variant="secondary" loading={busy} disabled={busy || code.replace(/[\s-]/g, '').length < 8} onPress={async () => {
        const r = await redeem(code);
        if (!r) return;
        setCode('');
        notify(r.gift
          ? t('plan.giftRedeemedYou', { plan: planName(t, r.gift.product), date: new Date(r.gift.until).toLocaleDateString() })
          : t('plan.joinedYou'));
        onDone?.();
      }} />
      {error ? <Caption color={p.danger}>{t(`plan.errors.${error}` as 'plan.errors.generic')}</Caption> : null}
    </Section>
  );
}

export function SignInFirst() {
  const { t } = useTranslation();
  const nav = useNav();
  return (
    <Section>
      <Body secondary>{t('plan.signInFirst')}</Body>
      <Button label={t('account.title')} icon="user" variant="secondary" onPress={() => nav.navigate('Account')} />
    </Section>
  );
}
